<template>
  <div>
    <PreLoaderSection
      :options="{ 'height': '500', enabledTwoColumns: true }"
      v-show="state.isPreLoaderShowing" />
    <Section class="summary-total-chart-container" v-show="!state.isPreLoaderShowing">
      <div class="section-header" slot="header">
        <h1 class="title">Total Summary</h1>
      </div>
      <div class="section-body">
        <div class="row">
          <div class="col-md-4">
            <div class="card-container">
              <SummaryCard :onClick="onSelectPlays" :cardStyle="playsCardStyle">
                <i class="fa fa-play fade-icon --active" />
                <h1 class="header-plays" :class="{ '--active': activeSeries === 'plays' }" v-if="!totalPlayEmpty && !state.isLoading">
                  {{ totalPlay.totalPlay }}
                </h1>
                <div class="spinner-border mb-2" role="status" v-if="totalPlayEmpty || state.isLoading" />
                <div class="subheader" :class="{ '--active': activeSeries === 'plays' }">TOTAL PLAYS</div>
              </SummaryCard>
            </div>
            <div class="card-container">
              <SummaryCard :onClick="onSelectWatchDuration" :cardStyle="watchDurationCardStyle">
                <i class="fa fa-clock fade-icon --active" />
                <h1
                  class="header-watch-duration"
                  :class="{ '--active': activeSeries === 'watchDuration' }"
                  v-if="!totalWatchDurationEmpty && !state.isLoading"
                >
                  {{ abbreviateLongNumbers(secondsToMinutes(totalWatchDuration.totalWatchDurationInSeconds)) }}<span class="subheader h5 pl-1" :class="{ '--active': activeSeries === 'watchDuration' }">min.</span>
                </h1>
                <div class="spinner-border mb-2" role="status" v-if="totalWatchDurationEmpty || state.isLoading" />
                <div class="subheader" :class="{ '--active': activeSeries === 'watchDuration' }">TOTAL WATCH DURATION</div>
              </SummaryCard>
            </div>
          </div>
          <div class="col-md-8">
            <div class="chart-container">
              <AreaChart
                :series="series"
                :categories="categories"
                :colors="areaChartLineColors"
                :dayChart="enableDayChart" />
            </div>
          </div>
        </div>
      </div>
    </Section>
  </div>
</template>

<script>
import _ from 'lodash';
import { getTotalPlay, getTotalWatchDuration } from '@/summary/api';
import generateLink from '@/utils/generateLink';
import dateFormatter from '@/utils/dateFormatter';
import numberUtils from '@/utils/numberUtils';

import Section from '@/components/Section.vue';
import SummaryCard from '@/summary/SummaryCard.vue';
import PreLoaderSection from '@/components/PreLoaderSection.vue';
import AreaChart from '@/charts/AreaChart.vue';

export default {
  mixins: [dateFormatter, numberUtils, generateLink],
  components: {
    Section,
    SummaryCard,
    PreLoaderSection,
    AreaChart,
  },
  data() {
    return {
      preloaderCustomClass: 'two-columns',
      mock: {},
      activeSeries: 'plays',
      totalPlay: [],
      playsSeries: [],
      playsCategories: [],
      totalWatchDuration: [],
      watchDurationSeries: [],
      watchDurationCategories: [],
      state: {
        isLoading: false,
        isPreLoaderShowing: true,
      },
      // For AreaChart Props
      series: [],
      categories: [],
      enableDayChart: false,
    };
  },
  computed: {
    startDate() {
      return this.$route.query.startDate;
    },
    endDate() {
      return this.$route.query.endDate;
    },
    shouldShowChart() {
      return false;
    },
    areaChartLineColors() {
      if (this.activeSeries === 'plays') return ['#FF9548'];
      return ['#0097C1'];
    },
    playsCardStyle() {
      if (this.activeSeries === 'plays') {
        return {
          background: '#FF9548',
          color: 'white',
        };
      }
      return {};
    },
    watchDurationCardStyle() {
      if (this.activeSeries === 'watchDuration') {
        return {
          background: '#0097C1',
          color: 'white',
        };
      }
      return {};
    },
    totalPlayEmpty() {
      return _.isEmpty(this.totalPlay);
    },
    totalWatchDurationEmpty() {
      return _.isEmpty(this.totalWatchDuration);
    },
  },
  watch: {
    '$route.query': {
      async handler(queryString) {
        const filter = this.generateQuery(queryString);
        await this.loadData(filter);
        await this.initChartSeries();
        await this.initChartCategories();
        await this.reassignSeriesAndCategories();
      },
    },
  },
  async created() {
    const filter = this.generateQuery(this.$route.query);
    await this.loadData(filter);
    await this.initChartSeries();
    await this.initChartCategories();
    this.series = this.playsSeries;
    this.state.isPreLoaderShowing = false;
  },
  methods: {
    async loadData(filter) {
      this.state.isLoading = true;
      await Promise.all([
        this.loadTotalPlay(filter),
        this.loadTotalWatchDuration(filter),
      ]);
      if (filter.range === 'today' || filter.range === 'last24hours' || !filter.range) {
        this.enableDayChart = true;
      } else {
        this.enableDayChart = false;
      }
      this.state.isLoading = false;
    },
    async loadTotalPlay(filter) {
      const response = await getTotalPlay(filter);
      this.totalPlay = response.data;
    },
    async loadTotalWatchDuration(filter) {
      const response = await getTotalWatchDuration(filter);
      this.totalWatchDuration = response.data;
    },
    initChartSeries() {
      const playsData = _.map(this.totalPlay.data, i => i.totalPlay);
      this.playsSeries = [{
        name: 'Total Video Plays',
        data: playsData,
      }];
      const watchDurationData = _.map(this.totalWatchDuration.data, i => this.secondsToMinutes(i.totalWatchDurationInSeconds));
      this.watchDurationSeries = [{
        name: 'Total Watch Duration',
        data: watchDurationData,
      }];
    },
    initChartCategories() {
      this.playsCategories = _.map(this.totalPlay.data, i => i.date);
      this.watchDurationCategories = _.map(this.totalWatchDuration.data, i => i.date);
      this.categories = this.playsCategories;
    },
    onSelectPlays() {
      this.activeSeries = 'plays';
      this.series = this.playsSeries;
      this.categories = this.playsCategories;
    },
    onSelectWatchDuration() {
      this.activeSeries = 'watchDuration';
      this.series = this.watchDurationSeries;
      this.categories = this.watchDurationCategories;
    },
    reassignSeriesAndCategories() {
      if (this.activeSeries === 'plays') this.onSelectPlays();
      else this.onSelectWatchDuration();
    },
    secondsToMinutes(time) {
      return Math.round(time / 60);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/variables.scss';

.header-plays {
  margin: 0;
  color: #FF9548;
}
.header-plays.--active {
  color: white;
}
.header-watch-duration {
  margin: 0;
  color: #0097C1;
}
.header-watch-duration.--active {
  color: white;
}
.subheader {
  color: $gray-500;
}
.subheader.--active {
  color: white;
}

.fade-icon {
  position: absolute;
  height: 45px;
  width: 45px;
  font-size: 45px;
  right: $spacer;
  opacity: .2;
}

.fade-icon.--active {
  opacity: .7;
}

.card-container {
  margin-top: $spacer * 1.25;
}
</style>
